import { useEffect } from "react";
import delaLatin from "@/assets/fonts/dela-latin.woff2?url";
import delaLatinExt from "@/assets/fonts/dela-latin-ext.woff2?url";
import geistMonoLatin from "@/assets/fonts/geist-mono-latin.woff2?url";
import geistMonoLatinExt from "@/assets/fonts/geist-mono-latin-ext.woff2?url";

/**
 * Webfonts nur dort laden, wo sie gebraucht werden, und nur von unserem eigenen Server.
 *
 * WARUM nur je Route (Audit 18.09.2026): index.html lud Dela Gothic One und Geist Mono global
 * per preload, auf jeder Route. Auf den Landingpages griff keine der beiden Schriften
 * (`.font-headline` ist in index.css auf Helvetica gesetzt; `font-display` und `font-mono`
 * kommen dort nicht vor), trotzdem kosteten sie 31 KB plus zwei zusätzliche Verbindungen im
 * kritischen Pfad. Genutzt werden sie im Admin (beide), im Kundenportal (Geist Mono für Zahlen
 * und Codes), auf der Hundeseite und im Service-Finder (Dela Gothic One).
 *
 * WARUM selbst gehostet (Datenschutz, KA-22, 08.10.2026): Bis dahin kamen die Dateien von
 * fonts.googleapis.com / fonts.gstatic.com, dabei ging die IP-Adresse an Google. Jetzt liegen
 * die woff2-Dateien in src/assets/fonts (Lizenz: SIL OFL 1.1, siehe OFL-*.txt daneben), Vite
 * hängt einen Hash an den Dateinamen (lange cachebar), und es gibt keine Fremdverbindung mehr.
 * Nur die Schnitte, die wir nutzen: Dela Gothic One 400, Geist Mono 400 + 500 (eine variable
 * Datei), je Subset latin und latin-ext. latin-ext lädt der Browser nur, wenn ein Zeichen
 * daraus auf der Seite steht (unicode-range).
 *
 * Verhalten: Beim ersten Aufruf je Schrift hängen wir die @font-face-Regeln als <style> in den
 * <head> und preloaden den kritischen Schnitt (latin). `font-display: swap`: der Text steht
 * sofort in der Rückfallschrift (Arial Black bzw. SF Mono/Monaco aus tailwind.config.ts) und
 * wechselt, sobald die Datei da ist. Ein zweiter Aufruf ist ein No-op; ein Unmount entfernt
 * nichts (die Schrift ist dann ohnehin gecacht und die nächste Route braucht sie vielleicht).
 */

export type Webfont = "dela" | "geistMono";

const LATIN =
  "U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD";
const LATIN_EXT =
  "U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C4, U+2113, U+2C60-2C7F, U+A720-A7FF";

type FontDef = { family: string; weight: string; latin: string; latinExt: string };

const FONTS: Record<Webfont, FontDef> = {
  dela: { family: "Dela Gothic One", weight: "400", latin: delaLatin, latinExt: delaLatinExt },
  // Variable Datei: deckt 400 und 500 ab.
  geistMono: { family: "Geist Mono", weight: "400 500", latin: geistMonoLatin, latinExt: geistMonoLatinExt },
};

function fontFace(f: FontDef, url: string, range: string): string {
  return `@font-face{font-family:'${f.family}';font-style:normal;font-weight:${f.weight};font-display:swap;src:url(${url}) format('woff2');unicode-range:${range};}`;
}

const geladen = new Set<Webfont>();

/** Lädt die Schrift, falls noch nicht geschehen. Sicher mehrfach aufrufbar. */
export function ladeWebfont(font: Webfont): void {
  if (geladen.has(font) || typeof document === "undefined") return;
  geladen.add(font);
  const f = FONTS[font];

  // Kritischer Schnitt (latin) sofort holen, nicht erst beim ersten Layout mit der Schrift.
  const preload = document.createElement("link");
  preload.rel = "preload";
  preload.as = "font";
  preload.type = "font/woff2";
  preload.href = f.latin;
  preload.crossOrigin = "anonymous";
  document.head.appendChild(preload);

  const style = document.createElement("style");
  style.dataset.webfont = font;
  style.textContent = fontFace(f, f.latinExt, LATIN_EXT) + fontFace(f, f.latin, LATIN);
  document.head.appendChild(style);
}

/** Hook-Variante für Seiten und Layouts: lädt die angegebenen Schriften beim Mount. */
export function useWebfonts(...fonts: Webfont[]): void {
  useEffect(() => {
    for (const f of fonts) ladeWebfont(f);
    // Bewusst kein Cleanup, siehe Kopfkommentar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, fonts);
}
